import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

import { determineJenjangByEmail } from '../helper'

import type { WebinarEntry, WebinarModule } from 'api/microlearning/types'

import 'dayjs/locale/id'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.locale('id')

export const WEBINAR_THRESHOLD_DEADLINE_MINUTES = 30

export const getWebinarTopicData = (
  originalTopicData: { topikTitle?: string; topikId?: number },
  topicIds: number[],
  webinarModules: WebinarModule[]
): { topikTitle?: string; topikId?: number } | null => {
  if (!!originalTopicData.topikId && !!originalTopicData.topikTitle) {
    return {
      topikTitle: originalTopicData.topikTitle,
      topikId: originalTopicData.topikId,
    }
  }
  const topicData = webinarModules.reduce<
    { topikTitle: string; topikId: number }[]
  >((topicData, module) => {
    if (topicIds.includes(module.topikId)) {
      return [
        ...topicData,
        { topikTitle: module.topikTitle, topikId: module.topikId },
      ]
    }
    return topicData
  }, [])
  /** Return null if user has more than topic title (user is multi-jenjang) */
  if (topicData.length > 1) return null

  const [topicDatum] = topicData
  return topicDatum
}

export const getUserEmailFromSession = (
  userSession: any
): string | undefined => {
  return userSession?.[0]?.user?.email
}

export const isTestingBelajarEmail = (email?: string): boolean => {
  const subdomain = email?.split('@')[1]

  return subdomain === 'testing.belajar.id'
}

export const isDeletedWebinar = (webinar: WebinarEntry): boolean => {
  return Boolean(webinar?.softDelete || webinar?.deletedAt)
}

export const isUpcomingWebinar = (
  webinar: WebinarEntry,
  wibTime = dayjs().tz('Asia/Jakarta')
): boolean => {
  return wibTime.isBefore(
    dayjs(webinar.endDate).add(WEBINAR_THRESHOLD_DEADLINE_MINUTES, 'minute')
  )
}

export const isWebinarVisibleForEmail = (
  webinar: WebinarEntry,
  email?: string
): boolean => {
  if (isTestingBelajarEmail(email)) return true

  const userJenjang = determineJenjangByEmail(email)
  if (!userJenjang) return false

  return (webinar?.module || []).some((module) => {
    return module?.jenjang?.includes(userJenjang)
  })
}

export const filterVisibleWebinars = (
  webinars: WebinarEntry[],
  email?: string
): WebinarEntry[] => {
  return (webinars || []).filter((webinar) => {
    return (
      !isDeletedWebinar(webinar) && isWebinarVisibleForEmail(webinar, email)
    )
  })
}

export const filterUpcomingWebinars = (
  webinars: WebinarEntry[],
  wibTime = dayjs().tz('Asia/Jakarta')
): WebinarEntry[] => {
  return (webinars || []).filter((webinar) =>
    isUpcomingWebinar(webinar, wibTime)
  )
}

export const filterAvailableWebinars = (
  webinars: WebinarEntry[],
  email?: string,
  wibTime = dayjs().tz('Asia/Jakarta')
): WebinarEntry[] => {
  return filterUpcomingWebinars(filterVisibleWebinars(webinars, email), wibTime)
}
